import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import PencilEdit from '@/assets/icons/pencil-edit.svg';
import PlusSm from '@/assets/icons/plus-sm.svg';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { useOpeningContext } from '@/contexts/opening-context';
import { formatShortDate } from '@/utils/date-util';

/**
 * Pure helper that decides whether to show the banner and returns
 * all data needed to render it.
 *
 * Inputs:
 * - daysLeft: number | null
 * - actualHires: number | null
 * - expectedHires: number | null
 * - endDate: string (already formatted)
 * - intl: react-intl's intl object for formatting
 *
 * Returns:
 * - { title?: string, description?: string, isSuccess?: boolean }
 */

function getHiringBannerData({
  daysLeft,
  actualHires,
  expectedHires,
  endDate,
  intl,
}: {
  daysLeft: number | null;
  actualHires: number | null;
  expectedHires: number | null;
  endDate: string;
  intl: IntlShape;
}) {
  const hasHiresInfo = actualHires != null && expectedHires != null;
  const goalEnded = daysLeft === 0;
  const meetsTarget = hasHiresInfo && actualHires >= expectedHires;
  const missesTarget = hasHiresInfo && actualHires < expectedHires;
  const endsTomorrow = daysLeft === 1;

  if (goalEnded && missesTarget) {
    return {
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal ended without reaching target',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal ended on {endDate} with {actualHires} out of {expectedHires} hires. Kindly create a new goal if your requirement is still open.',
        },
        { endDate, actualHires, expectedHires },
      ),
      isSuccess: false,
    };
  }

  if (goalEnded && meetsTarget) {
    return {
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal completed and target met!.',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal ended on {endDate}. Create a new goal to continue tracking sourcing spend.',
        },
        { endDate },
      ),
      isSuccess: true,
    };
  }

  if (endsTomorrow) {
    return {
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal ends in 1 day',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal will end on {endDate}. Kindly consider extending the end date if the requirement is still open.',
        },
        { endDate },
      ),
      isSuccess: false,
    };
  }

  return null;
}

function PerformanceStatusBanner() {
  const intl = useIntl();
  const { hiringGoal, hiringGoalUrl } = useOpeningContext();

  if (!hiringGoal) return null;

  // Basic values
  const expectedDuration = hiringGoal.duration;
  const elapsedDays = hiringGoal.days_elapsed;
  const daysLeft =
    expectedDuration != null && elapsedDays != null
      ? expectedDuration - elapsedDays
      : null;

  const endDate = formatShortDate(hiringGoal.hire_by);
  const actualHires = hiringGoal.hire_stats?.actual;
  const expectedHires = hiringGoal.hire_stats?.expected;
  const endsTomorrow = daysLeft === 1;

  const bannerData = getHiringBannerData({
    daysLeft,
    actualHires,
    expectedHires,
    endDate,
    intl,
  });

  if (!bannerData) return null;

  const { title, description, isSuccess = false } = bannerData;

  return (
    <Banner
      color={isSuccess ? 'green' : 'amber'}
      title={title}
      description={description}
      button={
        <Button variant="secondary" asChild>
          <Link to={String(hiringGoalUrl)} target="_blank">
            <img
              src={endsTomorrow ? PencilEdit : PlusSm}
              alt={endsTomorrow ? 'Edit' : 'Add'}
            />
            {endsTomorrow ? (
              <FormattedMessage defaultMessage="Edit Goal" />
            ) : (
              <FormattedMessage defaultMessage="New Goal" />
            )}
          </Link>
        </Button>
      }
    />
  );
}

export { PerformanceStatusBanner };
